import { useState, useEffect, useCallback, useMemo } from 'react';
import api from '../services/api';
import ProgramEditorTabs from './ProgramEditorTabs';
import {
  Settings, Save, ShieldCheck, Calendar, Info, Plus, Trash2, Pencil, Link2, Trophy,
} from 'lucide-react';
import { formatCycleDate, computeCycleEnd, resolveCycleEnd, pickDefaultCycleId } from '../utils/cycleUtils';
import './d28d/d28d-admin-surfaces.css';

// Panel admin del módulo D28D. Permite operar el catálogo de programas
// (Vital, Pancitas, Virtual y los que se creen) y los ciclos de 28 días
// que les aplican. Todo se persiste vía /api/programs y /api/cycles.

const emptyProgramForm = {
  id: '',
  name: '',
  color: '#10b981',
  active: true,
  active_cycle_id: 1,
  zoom_email: '',
  welcome_video_url: '',
};

const emptyCycleForm = {
  name: '',
  startDate: '',
  label: '',
  program_ids: [],
  start_video_url: '',
};

export default function AdminProgramsManager({ onBack, initialProgramId }) {
  const [programs, setPrograms] = useState([]);
  const [cycles, setCycles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');

  // Estado de edición de programa
  const [editingProgram, setEditingProgram] = useState(null);
  const [programForm, setProgramForm] = useState(emptyProgramForm);
  const [creatingProgram, setCreatingProgram] = useState(false);

  // Estado de edición de ciclo
  const [editingCycleId, setEditingCycleId] = useState(null);
  const [cycleForm, setCycleForm] = useState(emptyCycleForm);
  const [creatingCycle, setCreatingCycle] = useState(false);
  const [bulkBusy, setBulkBusy] = useState(false);

  const programsForCycleRow = useCallback((cycle) => {
    if (cycle.program_ids?.length) {
      return programs.filter((p) => cycle.program_ids.includes(String(p.id)) || cycle.program_ids.includes(p.id));
    }
    return programs.filter((p) => Number(p.active_cycle_id) === Number(cycle.id));
  }, [programs]);

  const sortedCyclesByStart = useMemo(
    () => [...cycles].sort((a, b) => String(a.startDate).localeCompare(String(b.startDate))),
    [cycles],
  );

  const resolvePreviousCycleVideoUrl = useCallback(() => {
    if (!sortedCyclesByStart.length) return '';
    if (editingCycleId) {
      const idx = sortedCyclesByStart.findIndex((c) => Number(c.id) === Number(editingCycleId));
      if (idx > 0) return sortedCyclesByStart[idx - 1].start_video_url || '';
      return '';
    }
    if (cycleForm.startDate) {
      const prev = sortedCyclesByStart.filter((c) => String(c.startDate) < cycleForm.startDate).pop();
      return prev?.start_video_url || '';
    }
    return sortedCyclesByStart[sortedCyclesByStart.length - 1]?.start_video_url || '';
  }, [sortedCyclesByStart, editingCycleId, cycleForm.startDate]);

  const fetchPrograms = useCallback(async () => {
    try {
      const res = await api.get('/programs', { params: { platform_only: 'true' } });
      setPrograms(res.data?.data || []);
    } catch {
      setError('Error al cargar programas');
    }
  }, []);

  const fetchCycles = useCallback(async () => {
    try {
      const res = await api.get('/cycles', { params: { platform_only: 'true' } });
      setCycles(res.data?.data || []);
    } catch {
      setError('Error al cargar ciclos');
    }
  }, []);

  useEffect(() => {
    (async () => {
      setLoading(true);
      await Promise.all([fetchPrograms(), fetchCycles()]);
      setLoading(false);
    })();
  }, [fetchPrograms, fetchCycles]);

  useEffect(() => {
    if (!initialProgramId || loading || programs.length === 0) return;
    const match = programs.find((p) => String(p.id) === String(initialProgramId));
    if (match) {
      setCreatingProgram(false);
      setEditingProgram(match);
      setProgramForm({
        id: match.id,
        name: match.name || '',
        color: match.color || '#10b981',
        active: match.active !== false,
        active_cycle_id: Number(match.active_cycle_id) || Number(pickDefaultCycleId(cycles)) || 1,
        zoom_email: match.zoom_email || '',
        welcome_video_url: match.welcome_video_url || '',
      });
    }
  }, [initialProgramId, loading, programs, cycles]);

  // Auto-clear de mensajes flash
  useEffect(() => {
    if (!message && !error) return;
    const t = setTimeout(() => { setMessage(''); setError(''); }, 4000);
    return () => clearTimeout(t);
  }, [message, error]);

  // ============================ PROGRAMAS ===================================

  const startEditProgram = (p) => {
    setCreatingProgram(false);
    setEditingProgram(p);
    setProgramForm({
      id: p.id,
      name: p.name || '',
      color: p.color || '#10b981',
      active: p.active !== false,
      active_cycle_id: Number(p.active_cycle_id) || Number(pickDefaultCycleId(cycles)) || 1,
      zoom_email: p.zoom_email || '',
      welcome_video_url: p.welcome_video_url || '',
    });
  };

  const startCreateProgram = () => {
    setEditingProgram(null);
    setCreatingProgram(true);
    setProgramForm({ ...emptyProgramForm, active_cycle_id: Number(pickDefaultCycleId(cycles)) || 1 });
  };

  const cancelProgramForm = () => {
    setEditingProgram(null);
    setCreatingProgram(false);
    setProgramForm(emptyProgramForm);
  };

  const submitProgram = async (e) => {
    e.preventDefault();
    setError(''); setMessage('');
    try {
      if (creatingProgram) {
        if (!programForm.name.trim()) {
          setError('El nombre del programa es obligatorio');
          return;
        }
        const createRes = await api.post('/programs', {
          name: programForm.name.trim(),
          color: programForm.color,
          active: programForm.active,
          active_cycle_id: Number(programForm.active_cycle_id) || null,
          zoom_email: programForm.zoom_email.trim() || undefined,
          welcome_video_url: programForm.welcome_video_url?.trim() || null,
        });
        const created = createRes.data?.data;
        const invite = createRes.data?.default_invite;
        if (invite?.code) {
          setMessage(`Programa creado (ID: ${created?.id || '—'}). Código: ${invite.code} · Etiqueta: ${invite.label || '—'}`);
        } else {
          setMessage(`Programa creado (ID: ${created?.id || '—'})`);
        }
      } else if (programForm.id) {
        await api.put(`/programs/${programForm.id}`, {
          name: programForm.name.trim(),
          color: programForm.color,
          active: programForm.active,
          active_cycle_id: Number(programForm.active_cycle_id) || null,
          zoom_email: programForm.zoom_email.trim() || undefined,
          welcome_video_url: programForm.welcome_video_url?.trim() || null,
        });
        setMessage('Programa actualizado');
      }
      cancelProgramForm();
      await fetchPrograms();
    } catch (err) {
      setError(err?.response?.data?.error || 'Error guardando programa');
    }
  };

  const toggleProgramActive = async (p) => {
    setError(''); setMessage('');
    try {
      await api.put(`/programs/${p.id}`, { active: !p.active });
      await fetchPrograms();
    } catch (err) {
      setError(err?.response?.data?.error || 'Error cambiando estado');
    }
  };

  const deleteProgram = async (p) => {
    if (!window.confirm(`¿Borrar el programa "${p.name}"? Esta acción no se puede deshacer.`)) return;
    setError(''); setMessage('');
    try {
      await api.delete(`/programs/${p.id}`);
      setMessage('Programa eliminado');
      await fetchPrograms();
    } catch (err) {
      setError(err?.response?.data?.error || 'Error borrando programa');
    }
  };

  // ============================ CICLOS ======================================

  const startEditCycle = (c) => {
    setCreatingCycle(false);
    setEditingCycleId(c.id);
    const linked = c.program_ids?.length
      ? [...c.program_ids]
      : programs.filter((p) => Number(p.active_cycle_id) === Number(c.id)).map((p) => p.id);
    setCycleForm({
      name: c.name || `Ciclo ${c.id}`,
      startDate: c.startDate || '',
      label: c.label || '',
      program_ids: linked,
      start_video_url: c.start_video_url || '',
    });
  };

  const startCreateCycle = () => {
    setEditingCycleId(null);
    setCreatingCycle(true);
    setCycleForm({
      ...emptyCycleForm,
      program_ids: programs.map((p) => p.id),
    });
  };

  const cancelCycleForm = () => {
    setEditingCycleId(null);
    setCreatingCycle(false);
    setCycleForm(emptyCycleForm);
  };

  const submitCycle = async (e) => {
    e.preventDefault();
    setError(''); setMessage('');
    if (!cycleForm.startDate) {
      setError('La fecha de inicio del ciclo es obligatoria');
      return;
    }
    try {
      const payload = {
        name: cycleForm.name,
        startDate: cycleForm.startDate,
        label: cycleForm.label,
        program_ids: cycleForm.program_ids || [],
        start_video_url: cycleForm.start_video_url?.trim() || null,
      };
      if (creatingCycle) {
        await api.post('/cycles', payload);
        setMessage('Ciclo creado');
      } else if (editingCycleId) {
        await api.put(`/cycles/${editingCycleId}`, payload);
        setMessage('Ciclo actualizado');
      }
      cancelCycleForm();
      await Promise.all([fetchCycles(), fetchPrograms()]);
    } catch (err) {
      setError(err?.response?.data?.error || 'Error guardando ciclo');
    }
  };

  const deleteCycle = async (c) => {
    if (!window.confirm(`¿Borrar "${c.name}" (inicia ${formatCycleDate(c.startDate)})?`)) return;
    setError(''); setMessage('');
    try {
      await api.delete(`/cycles/${c.id}`);
      setMessage('Ciclo eliminado');
      await Promise.all([fetchCycles(), fetchPrograms()]);
    } catch (err) {
      setError(err?.response?.data?.error || 'Error borrando ciclo');
    }
  };

  const toggleCycleProgram = (programId) => {
    setCycleForm((prev) => {
      const ids = prev.program_ids || [];
      const has = ids.includes(programId);
      return {
        ...prev,
        program_ids: has ? ids.filter((id) => id !== programId) : [...ids, programId],
      };
    });
  };

  const bulkAssignAllPrograms = async () => {
    if (!window.confirm('¿Asignar TODOS los programas a TODOS los ciclos? Esto no quita programas de otros ciclos: cada ciclo tendrá la lista completa.')) return;
    setBulkBusy(true);
    setError('');
    setMessage('');
    try {
      const res = await api.post('/cycles/bulk/assign-all-programs');
      setMessage(res.data?.message || 'Programas asignados a todos los ciclos');
      await fetchCycles();
    } catch (err) {
      setError(err?.response?.data?.error || 'Error en asignación masiva');
    } finally {
      setBulkBusy(false);
    }
  };

  const copyPremioFromCycle2 = async () => {
    if (!window.confirm('¿Copiar la premiación configurada en el Ciclo 2 al resto de ciclos? Se crearán o actualizarán las premiaciones con las mismas reglas y premio.')) return;
    setBulkBusy(true);
    setError('');
    setMessage('');
    try {
      const res = await api.post('/cycles/bulk/copy-premio/2');
      setMessage(res.data?.message || 'Premiación copiada');
    } catch (err) {
      setError(err?.response?.data?.error || 'Error copiando premiación');
    } finally {
      setBulkBusy(false);
    }
  };

  // ============================ RENDER ======================================

  if (loading) {
    return (
      <div className="dashboard-main-view surface-readable d28d-admin-surface p-8 text-center d28d-text-muted">
        Cargando programas y ciclos...
      </div>
    );
  }

  const renderProgramForm = () => (
    <form onSubmit={submitProgram} className="card rounded-3xl shadow-sm p-5 mb-6 space-y-4">
      <div className="flex items-center justify-between border-b border-slate-200 pb-2">
        <h4 className="text-md font-semibold">
          {creatingProgram ? 'Nuevo programa D28D' : `Editar programa: ${programForm.name || programForm.id}`}
        </h4>
        <button type="button" className="btn-secondary text-xs" onClick={cancelProgramForm}>Cerrar</button>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="label">Nombre del programa</label>
          <input
            className="input"
            value={programForm.name}
            onChange={(e) => setProgramForm({ ...programForm, name: e.target.value })}
            placeholder="Vital, Pancitas, Virtual D28D..."
            required
          />
        </div>
        <div>
          <label className="label">Color</label>
          <div className="flex items-center gap-2">
            <input
              type="color"
              value={programForm.color}
              onChange={(e) => setProgramForm({ ...programForm, color: e.target.value })}
              className="h-10 w-14 rounded border border-stone-300 cursor-pointer"
            />
            <input
              className="input flex-1"
              value={programForm.color}
              onChange={(e) => setProgramForm({ ...programForm, color: e.target.value })}
              placeholder="#10b981"
            />
          </div>
        </div>
        <div>
          <label className="label">Ciclo activo</label>
          <select
            className="input"
            value={programForm.active_cycle_id}
            onChange={(e) => setProgramForm({ ...programForm, active_cycle_id: Number(e.target.value) })}
          >
            <option value="">— Sin asignar —</option>
            {cycles.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name} — inicia {formatCycleDate(c.startDate)}{c.label ? ` · ${c.label}` : ''}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="label">Email Zoom (opcional)</label>
          <input
            className="input"
            value={programForm.zoom_email}
            onChange={(e) => setProgramForm({ ...programForm, zoom_email: e.target.value })}
            placeholder="zoom@d28d.com"
          />
          <p className="text-xs text-stone-500 mt-1">La contraseña Zoom NUNCA se guarda aquí. Se inyecta desde el servidor con la variable de entorno correspondiente.</p>
        </div>
        <div className="md:col-span-2">
          <label className="label">Video de bienvenida (programa)</label>
          <input
            className="input"
            type="url"
            value={programForm.welcome_video_url || ''}
            onChange={(e) => setProgramForm({ ...programForm, welcome_video_url: e.target.value })}
            placeholder="https://youtube.com/... o Zoom share"
          />
          <p className="text-xs text-stone-500 mt-1">
            Lo ven los usuarios finales de este programa en Inicio. Las sedes pueden sobreescribirlo en Editar sede.
            Si está vacío, se usa el video del ciclo (legado).
          </p>
        </div>
        <div className="md:col-span-2 flex items-center gap-3">
          <label className="inline-flex items-center gap-2 text-sm text-stone-700">
            <input
              type="checkbox"
              checked={!!programForm.active}
              onChange={(e) => setProgramForm({ ...programForm, active: e.target.checked })}
            />
            Programa activo (visible para usuarios)
          </label>
        </div>
      </div>
      <div className="flex justify-end gap-2">
        <button type="button" className="btn-secondary" onClick={cancelProgramForm}>Cancelar</button>
        <button type="submit" className="btn-primary inline-flex items-center gap-2">
          <Save className="w-4 h-4" /> Guardar
        </button>
      </div>
    </form>
  );

  const renderCycleForm = () => (
    <form onSubmit={submitCycle} className="bg-white rounded-3xl shadow-sm border border-slate-200 p-5 mb-6 space-y-4">
      <div className="flex items-center justify-between border-b border-slate-200 pb-2">
        <h4 className="text-md font-semibold text-stone-900">
          {creatingCycle ? 'Nuevo ciclo' : `Editar ciclo: ${cycleForm.name}`}
        </h4>
        <button type="button" className="btn-secondary text-xs" onClick={cancelCycleForm}>Cerrar</button>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div>
          <label className="label">Nombre del ciclo</label>
          <input
            className="input"
            value={cycleForm.name}
            onChange={(e) => setCycleForm({ ...cycleForm, name: e.target.value })}
            placeholder="Ciclo 14"
          />
        </div>
        <div>
          <label className="label">Fecha de inicio</label>
          <input
            type="date"
            className="input"
            value={cycleForm.startDate}
            onChange={(e) => setCycleForm({ ...cycleForm, startDate: e.target.value })}
            required
          />
          {cycleForm.startDate && (
            <p className="text-xs text-stone-500 mt-1">
              Termina el {formatCycleDate(computeCycleEnd(cycleForm.startDate))} (28 días).
            </p>
          )}
        </div>
        <div>
          <label className="label">Etiqueta (opcional)</label>
          <input
            className="input"
            value={cycleForm.label}
            onChange={(e) => setCycleForm({ ...cycleForm, label: e.target.value })}
            placeholder="Vacacional, Especial..."
          />
        </div>
      </div>
      <div className="border-t border-slate-200 pt-4 space-y-2">
        <div className="flex flex-wrap items-end justify-between gap-2">
          <label className="label mb-0">Video de inicio de ciclo (YouTube / Vimeo)</label>
          <button
            type="button"
            className="btn-secondary text-xs inline-flex items-center gap-1"
            disabled={!resolvePreviousCycleVideoUrl()}
            onClick={() => setCycleForm((prev) => ({
              ...prev,
              start_video_url: resolvePreviousCycleVideoUrl(),
            }))}
          >
            <Link2 className="w-3.5 h-3.5" />
            Copiar del ciclo anterior
          </button>
        </div>
        <input
          className="input"
          value={cycleForm.start_video_url || ''}
          onChange={(e) => setCycleForm({ ...cycleForm, start_video_url: e.target.value })}
          placeholder="https://www.youtube.com/watch?v=..."
        />
        <p className="text-xs text-stone-500">
          Los participantes verán este video en Inicio al comenzar el ciclo. Déjalo vacío si no aplica.
        </p>
      </div>
      <div className="border-t border-slate-200 pt-4 space-y-3">
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <label className="label mb-0">Programas en este ciclo</label>
          <div className="flex gap-2 text-xs">
            <button
              type="button"
              className="btn-secondary text-xs"
              onClick={() => setCycleForm((prev) => ({ ...prev, program_ids: programs.map((p) => p.id) }))}
            >
              Seleccionar todos
            </button>
            <button
              type="button"
              className="btn-secondary text-xs"
              onClick={() => setCycleForm((prev) => ({ ...prev, program_ids: [] }))}
            >
              Quitar todos
            </button>
          </div>
        </div>
        {programs.length === 0 ? (
          <p className="text-sm text-stone-500">No hay programas. Crea programas D28D primero.</p>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2">
            {programs.map((p) => (
              <label
                key={p.id}
                className="inline-flex items-center gap-2 rounded-xl border border-slate-200 bg-stone-50 px-3 py-2 text-sm text-stone-800 cursor-pointer hover:bg-stone-100"
              >
                <input
                  type="checkbox"
                  checked={(cycleForm.program_ids || []).includes(p.id)}
                  onChange={() => toggleCycleProgram(p.id)}
                />
                <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ backgroundColor: p.color || '#cbd5e1' }} />
                <span className="font-medium truncate">{p.name}</span>
              </label>
            ))}
          </div>
        )}
      </div>
      <div className="flex justify-end gap-2">
        <button type="button" className="btn-secondary" onClick={cancelCycleForm}>Cancelar</button>
        <button type="submit" className="btn-primary inline-flex items-center gap-2">
          <Save className="w-4 h-4" /> Guardar
        </button>
      </div>
    </form>
  );

  return (
    <div className="dashboard-main-view surface-readable d28d-admin-surface space-y-6">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          {onBack ? (
            <button type="button" className="btn-secondary panel-back-btn" onClick={onBack}>
              ← Operación
            </button>
          ) : null}
          <div className="p-3 bg-slate-100 rounded-2xl">
            <Settings className="w-6 h-6" />
          </div>
          <div>
            <h2 className="text-2xl font-bold d28d-page-title">Gestión de Programas D28D</h2>
            <p className="d28d-text-muted">Programas (Vital, Pancitas, Virtual…) y los ciclos de 28 días en los que operan.</p>
          </div>
        </div>
      </div>

      {error && <div className="bg-red-50 border border-red-200 text-red-700 p-3 rounded-xl text-sm">{error}</div>}
      {message && <div className="bg-emerald-50 border border-emerald-200 text-emerald-700 p-3 rounded-xl text-sm">{message}</div>}

      {/* ============================ PROGRAMAS ================================== */}
      <section>
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-lg font-semibold">Programas</h3>
          <button className="btn-primary inline-flex items-center gap-2" onClick={startCreateProgram}>
            <Plus className="w-4 h-4" /> Nuevo programa
          </button>
        </div>

        {(creatingProgram) && renderProgramForm()}

        {editingProgram && (
          <ProgramEditorTabs
            program={editingProgram}
            cycles={cycles}
            onClose={cancelProgramForm}
            onSaved={fetchPrograms}
          />
        )}

        {!editingProgram && (programs.length === 0 ? (
          <div className="card rounded-3xl border border-dashed p-8 text-center d28d-text-muted">
            Aún no hay programas. Crea el primero.
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {programs.map((p) => {
              const activeCycle = cycles.find((c) => Number(c.id) === Number(p.active_cycle_id));
              return (
                <div key={p.id} className="card rounded-3xl shadow-sm p-5">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2 min-w-0">
                      <span className="w-3 h-3 rounded-full flex-shrink-0" style={{ backgroundColor: p.color || '#cbd5e1' }} />
                      <h4 className="text-base font-bold truncate">{p.name}</h4>
                    </div>
                    <ShieldCheck className={`w-5 h-5 ${p.active ? 'text-lime-600' : 'text-slate-300'}`} />
                  </div>

                  <div className="ui-light-panel--muted p-3 rounded-2xl mb-3 text-xs">
                    <p className="text-[10px] font-bold d28d-text-muted uppercase tracking-wider">ID programa</p>
                    <p className="font-mono font-medium truncate">{p.id}</p>
                  </div>

                  <div className="ui-light-panel--muted p-3 rounded-2xl mb-3">
                    <p className="text-[10px] font-bold text-indigo-600 uppercase tracking-wider">Ciclo activo</p>
                    {activeCycle ? (
                      <>
                        <p className="text-lg font-bold">{activeCycle.name}</p>
                        <p className="text-xs d28d-text-muted">
                          {formatCycleDate(activeCycle.startDate)} → {formatCycleDate(resolveCycleEnd(activeCycle))}
                          {activeCycle.label ? ` · ${activeCycle.label}` : ''}
                        </p>
                      </>
                    ) : (
                      <p className="text-sm d28d-text-muted">Sin ciclo asignado</p>
                    )}
                  </div>

                  {p.zoom_email && (
                    <div className="ui-light-panel--muted p-3 rounded-2xl mb-3 text-xs">
                      <p className="text-[10px] font-bold d28d-text-muted uppercase tracking-wider">Email Zoom</p>
                      <p className="font-medium truncate">{p.zoom_email}</p>
                    </div>
                  )}

                  <div className="flex flex-wrap gap-2">
                    <button
                      type="button"
                      className="btn-secondary inline-flex items-center gap-1 text-xs"
                      onClick={() => startEditProgram(p)}
                    >
                      <Pencil className="w-3 h-3" /> Editar
                    </button>
                    <button
                      type="button"
                      className="btn-secondary inline-flex items-center gap-1 text-xs"
                      onClick={() => toggleProgramActive(p)}
                    >
                      {p.active ? 'Desactivar' : 'Activar'}
                    </button>
                    <button
                      type="button"
                      className="inline-flex items-center gap-1 px-3 py-1.5 rounded-2xl bg-red-600 hover:bg-red-700 text-white text-xs"
                      onClick={() => deleteProgram(p)}
                    >
                      <Trash2 className="w-3 h-3" /> Borrar
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        ))}
      </section>

      {/* ============================ CICLOS ================================== */}
      <section>
        <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
          <div className="flex items-center gap-2">
            <Calendar className="w-5 h-5" />
            <h3 className="text-lg font-semibold d28d-page-title">Ciclos de 28 días</h3>
          </div>
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              className="btn-secondary inline-flex items-center gap-2 text-sm"
              disabled={bulkBusy || programs.length === 0 || cycles.length === 0}
              onClick={bulkAssignAllPrograms}
            >
              <Link2 className="w-4 h-4" /> Asignar todos los programas a todos los ciclos
            </button>
            <button
              type="button"
              className="btn-secondary inline-flex items-center gap-2 text-sm"
              disabled={bulkBusy || cycles.length === 0}
              onClick={copyPremioFromCycle2}
            >
              <Trophy className="w-4 h-4" /> Copiar premiación del Ciclo 2
            </button>
            <button className="btn-primary inline-flex items-center gap-2" onClick={startCreateCycle}>
              <Plus className="w-4 h-4" /> Nuevo ciclo
            </button>
          </div>
        </div>

        <p className="text-xs d28d-text-muted mb-3 inline-flex items-center gap-2">
          <Info className="w-3.5 h-3.5" />
          Cada ciclo dura 28 días exactos (4 semanas). El sistema calcula automáticamente el fin a partir de la fecha de inicio.
        </p>

        {(creatingCycle || editingCycleId) && renderCycleForm()}

        <div className="overflow-x-auto border border-slate-200 rounded-xl bg-white admin-cycles-table">
            <table className="min-w-full text-sm">
              <thead className="bg-stone-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider text-stone-600">Ciclo</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider text-stone-600">Inicio</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider text-stone-600">Fin (auto)</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider text-stone-600">Etiqueta</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider text-stone-600">Video inicio</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider text-stone-600">Programas asociados</th>
                  <th className="px-6 py-3 text-right text-xs font-semibold uppercase tracking-wider text-stone-600">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {cycles.length === 0 ? (
                  <tr className="border-t border-stone-100">
                    <td colSpan="7" className="px-6 py-6 text-center text-sm text-stone-600">
                      No hay ciclos cargados. Crea el primero para empezar.
                    </td>
                  </tr>
                ) : cycles.map((c) => {
                  const inUse = programsForCycleRow(c);
                  return (
                    <tr key={c.id} className="border-t border-stone-100">
                      <td className="px-6 py-3 whitespace-nowrap font-medium text-stone-900">{c.name}</td>
                      <td className="px-6 py-3 whitespace-nowrap text-stone-700">{formatCycleDate(c.startDate)}</td>
                      <td className="px-6 py-3 whitespace-nowrap text-stone-700">{formatCycleDate(resolveCycleEnd(c))}</td>
                      <td className="px-6 py-3 whitespace-nowrap text-stone-700">{c.label || '—'}</td>
                      <td className="px-6 py-3 whitespace-nowrap text-xs text-stone-600 max-w-[10rem] truncate" title={c.start_video_url || ''}>
                        {c.start_video_url ? '✓ Configurado' : '—'}
                      </td>
                      <td className="px-6 py-3 whitespace-nowrap text-xs">
                        {inUse.length === 0 ? (
                          <span className="d28d-text-muted">—</span>
                        ) : (
                          <div className="flex flex-wrap gap-1">
                            {inUse.map((p) => (
                              <span key={p.id} className="px-2 py-0.5 rounded-full bg-indigo-100 text-indigo-800">
                                {p.name}
                              </span>
                            ))}
                          </div>
                        )}
                      </td>
                      <td className="px-6 py-3 whitespace-nowrap text-right">
                        <div className="inline-flex gap-2">
                          <button
                            type="button"
                            className="btn-secondary inline-flex items-center gap-1 text-xs"
                            onClick={() => startEditCycle(c)}
                          >
                            <Pencil className="w-3 h-3" /> Editar
                          </button>
                          <button
                            type="button"
                            className="inline-flex items-center gap-1 px-3 py-1.5 rounded-2xl bg-red-600 hover:bg-red-700 text-white text-xs"
                            onClick={() => deleteCycle(c)}
                          >
                            <Trash2 className="w-3 h-3" /> Borrar
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
        </div>
      </section>
    </div>
  );
}
